import { Component } from '@theme/component';
import { isClickedOutside, normalizeString, onAnimationEnd } from '@theme/utilities';

/** Set to false to restore normal form submit + redirect after testing. */
const LOCALIZATION_DEBUG_NO_REDIRECT = false;

/**
 * Toggles the country filter border when a scrollable country list is not at the top.
 *
 * @param {ParentNode} root
 * @param {HTMLElement | null} scrollElement
 */
function updateCountryFilterScrollState(root, scrollElement) {
  const countryFilter = root.querySelector('.country-filter');
  if (!(countryFilter instanceof HTMLElement) || !scrollElement) return;

  countryFilter.classList.toggle('is-scrolled', scrollElement.scrollTop > 0);
}

/**
 * A custom element that displays a localization form.
 *
 * @typedef {object} FormRefs
 * @property {HTMLDivElement} countryList - The country list element.
 * @property {HTMLInputElement} countryInput - The country input element.
 * @property {HTMLElement[]} countryListItems - The country list items.
 * @property {HTMLFormElement} form - The form element.
 * @property {HTMLDivElement} liveRegion - The live region element.
 * @property {HTMLInputElement | HTMLSelectElement} languageInput - The language input element.
 * @property {HTMLElement} noResultsMessage - The no results message element.
 * @property {HTMLUListElement} popularCountries - The popular countries element.
 * @property {HTMLInputElement} search - The search input element.
 * @property {HTMLButtonElement} resetButton - The reset button element.
 *
 * @extends {Component<FormRefs>}
 */
/**
 * @typedef {object} MarketConfigEntry
 * @property {string} countryCode - ISO 3166-1 alpha-2 country code.
 * @property {string[]} languages - Available language codes for the market.
 */

class LocalizationFormComponent extends Component {
  /** @type {MarketConfigEntry[]} */
  marketsConfig = [];

  /** @returns {FormRefs} */
  get formRefs() {
    return /** @type {FormRefs} */ (this.refs);
  }

  connectedCallback() {
    super.connectedCallback();

    this.marketsConfig = this.#parseMarketsConfig();

    this.formRefs.search && this.formRefs.search.addEventListener('keydown', this.#onSearchKeyDown);
    this.formRefs.countryList && this.formRefs.countryList.addEventListener('keydown', this.#onContainerKeyDown);
    this.formRefs.countryList && this.formRefs.countryList.addEventListener('scroll', this.#onCountryListScroll);

    if (LOCALIZATION_DEBUG_NO_REDIRECT && this.formRefs.form instanceof HTMLFormElement) {
      this.formRefs.form.addEventListener('submit', this.#onFormSubmit);
    }

    // Resizing the language input can be expensive for browsers that don't support field-sizing: content.
    // Splitting it into separate tasks helps when there are multiple localization forms on the page.
    setTimeout(() => this.resizeLanguageInput(), 0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.formRefs.search && this.formRefs.search.removeEventListener('keydown', this.#onSearchKeyDown);
    this.formRefs.countryList && this.formRefs.countryList.removeEventListener('keydown', this.#onContainerKeyDown);
    this.formRefs.countryList && this.formRefs.countryList.removeEventListener('scroll', this.#onCountryListScroll);

    if (this.formRefs.form instanceof HTMLFormElement) {
      this.formRefs.form.removeEventListener('submit', this.#onFormSubmit);
    }
  }

  /** @param {SubmitEvent} event */
  #onFormSubmit = (event) => {
    if (!LOCALIZATION_DEBUG_NO_REDIRECT) return;

    event.preventDefault();
    this.#submitLocalizationForm();
  };

  /**
   * @returns {{ url: string, method: string, payload: Record<string, FormDataEntryValue> } | null}
   */
  #getFormPayload() {
    const { form } = this.formRefs;
    if (!(form instanceof HTMLFormElement)) return null;

    const formData = new FormData(form);
    return {
      url: form.action,
      method: form.method || 'POST',
      payload: Object.fromEntries(formData.entries()),
    };
  }

  /**
   * Submits localization form. In debug mode sends request via fetch and skips redirect.
   */
  #submitLocalizationForm() {
    const { form } = this.formRefs;
    if (!(form instanceof HTMLFormElement)) return;

    if (!LOCALIZATION_DEBUG_NO_REDIRECT) {
      form.submit();
      return;
    }

    const data = this.#getFormPayload();
    if (!data) return;

    const { url, method } = data;
    const formData = new FormData(form);

    fetch(url, {
      method,
      body: formData,
      credentials: 'same-origin',
      redirect: 'manual',
    }).catch(() => {});
  }

  /**
   * Handles the keydown event for the container.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onContainerKeyDown = (event) => {
    const { countryInput, countryListItems, languageInput } = this.formRefs;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus('UP');
        break;
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus('DOWN');
        break;
      case 'Enter': {
        event.preventDefault();
        event.stopPropagation();
        const focusedItem = countryListItems.find((item) => item.getAttribute('aria-selected') === 'true');

        if (focusedItem?.dataset.languageCode && !focusedItem.dataset.value) {
          languageInput.value = focusedItem.dataset.languageCode;
          this.#submitLocalizationForm();
        } else if (focusedItem) {
          const iso = focusedItem.dataset.value ?? '';
          countryInput.value = iso;
          this.#syncLanguageToSelectedCountry(iso);
          this.#submitLocalizationForm();
        }
        break;
      }
    }

    if (!this.formRefs.search) return;

    setTimeout(() => {
      const focusableItems = this.formRefs.countryListItems.filter((item) => !item.hasAttribute('hidden'));
      const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
      const focusedItem = focusableItems[focusedItemIndex];

      if (focusedItem) {
        this.formRefs.search.setAttribute('aria-activedescendant', focusedItem.id);
      } else {
        this.formRefs.search.setAttribute('aria-activedescendant', '');
      }
    });
  };

  /**
   * Selects a country.
   *
   * @param {string} countryName - The name of the country to select.
   * @param {Event} event - The event object.
   */
  selectCountry = (countryName, event) => {
    event.preventDefault();
    const { countryInput } = this.formRefs;

    countryInput.value = countryName;
    this.#syncLanguageToSelectedCountry(countryName);
    this.#submitLocalizationForm();
  };

  /**
   * Selects a language without changing country/market.
   *
   * @param {Event} event - The click event.
   */
  selectLanguage = (event) => {
    event.preventDefault();
    const item = event.target instanceof HTMLElement ? event.target.closest('.localization-form__list-item') : null;
    const languageCode = item?.dataset.languageCode;
    const { languageInput } = this.formRefs;
    if (!languageInput || !languageCode) return;

    languageInput.value = languageCode;
    this.#submitLocalizationForm();
  };

  /**
   * @returns {MarketConfigEntry[]}
   */
  #parseMarketsConfig() {
    const raw = this.getAttribute('data-markets-config');

    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * Picks the best language from market config for a country ISO code.
   * Uses only server-provided `country.available_languages` (no hardcoded map).
   *
   * @param {string} countryIso - ISO 3166-1 alpha-2, e.g. DE.
   * @returns {string | null}
   */
  #primaryLanguageForMarket(countryIso) {
    const code = countryIso.trim().toUpperCase();
    const countryLower = code.toLowerCase();
    const entry = this.marketsConfig.find(
      (market) => market.countryCode?.trim().toUpperCase() === code
    );

    const languages = entry?.languages;
    if (!languages?.length) return null;

    const exactMatch = languages.find((lang) => lang.trim().toLowerCase() === countryLower);
    if (exactMatch) return exactMatch;

    const localeMatch = languages.find((lang) => {
      const parts = lang.trim().toLowerCase().split('-');
      return parts.includes(countryLower) || parts[0] === countryLower;
    });
    if (localeMatch) return localeMatch;

    return languages[0] ?? null;
  }

  /**
   * Sets language_code from server-provided market config so POST /localization receives a locale
   * valid for the destination market (e.g. DE → de). When the <select> options are still from
   * the previous market snapshot, a synthetic option is added for the target locale.
   *
   * @param {string} countryIso - Country ISO code (e.g. NL).
   */
  #syncLanguageToSelectedCountry(countryIso) {
    const { languageInput } = this.formRefs;
    if (!languageInput) return;

    const suggested = this.#primaryLanguageForMarket(countryIso);

    if (languageInput instanceof HTMLInputElement && languageInput.type === 'hidden') {
      if (suggested) languageInput.value = suggested;
      return;
    }

    if (!(languageInput instanceof HTMLSelectElement)) return;

    const languageWasUpdated = this.#setSelectLanguage(languageInput, suggested);
    if (!languageWasUpdated) return;

    this.resizeLanguageInput();
  }

  /**
   * @param {HTMLSelectElement} select
   * @param {string | null} suggested
   * @returns {boolean} Whether the select value changed or was normalized.
   */
  #setSelectLanguage(select, suggested) {
    const options = Array.from(select.options);
    if (options.length === 0) return false;

    if (!suggested) {
      if (options.length <= 1) return false;

      this.#removeSyntheticLanguageOption(select);
      select.selectedIndex = 0;
      return true;
    }

    const matchingOption = this.#findLanguageOption(options, suggested);
    this.#removeSyntheticLanguageOption(select);

    if (matchingOption) {
      select.value = matchingOption.value;
      return true;
    }

    this.#ensureSyntheticLanguageOption(select, suggested);
    select.value = suggested;
    return true;
  }

  /**
   * @param {HTMLOptionElement[]} options
   * @param {string} suggested
   * @returns {HTMLOptionElement | undefined}
   */
  #findLanguageOption(options, suggested) {
    const nativeOptions = options.filter((option) => option.dataset.localizationSynthetic !== 'true');
    const exactMatch = nativeOptions.find((option) => option.value.toLowerCase() === suggested.toLowerCase());
    if (exactMatch) return exactMatch;

    return nativeOptions.find(
      (option) =>
        this.#localeValueMatchesBase(option.value, suggested) ||
        this.#localeValueMatchesBase(option.getAttribute('lang') || '', suggested)
    );
  }

  /**
   * @param {string} value - Locale or option value, e.g. fr-FR.
   * @param {string} base - Short language code, e.g. fr.
   */
  #localeValueMatchesBase(value, base) {
    const v = (value || '').toLowerCase().trim();
    const b = (base || '').toLowerCase().trim();
    if (!v || !b) return false;
    return v === b || v.startsWith(`${b}-`) || v.split('-')[0] === b;
  }

  /** @param {HTMLSelectElement} select */
  #removeSyntheticLanguageOption(select) {
    select.querySelector('option[data-localization-synthetic="true"]')?.remove();
  }

  /**
   * Ensures POST includes a locale code that exists only after navigation (options still from old market).
   *
   * @param {HTMLSelectElement} select
   * @param {string} value - language_code to submit (e.g. fr, de).
   */
  #ensureSyntheticLanguageOption(select, value) {
    let synthetic =
      /** @type {HTMLOptionElement | null} */ (select.querySelector('option[data-localization-synthetic="true"]'));
    if (!synthetic) {
      synthetic = document.createElement('option');
      synthetic.dataset.localizationSynthetic = 'true';
      select.appendChild(synthetic);
    }
    synthetic.value = value;
    synthetic.setAttribute('lang', value);
    synthetic.textContent = value;
  }

  /**
   * Changes the language of the localization form.
   *
   * @param {Event} event - The event object.
   */
  changeLanguage(event) {
    const { languageInput } = this.formRefs;
    const value = event.target instanceof HTMLSelectElement ? event.target.value : null;

    if (value) {
      languageInput.value = value;
      this.resizeLanguageInput();
      this.#submitLocalizationForm();
    }
  }

  resizeLanguageInput() {
    const { languageInput } = this.formRefs;

    if (!languageInput || !(languageInput instanceof HTMLSelectElement) || CSS.supports('field-sizing', 'content')) return;

    // Hide all options except the selected option
    for (const option of languageInput.options) {
      if (!option.selected) {
        option.dataset.optionLabel = option.textContent || '';
        option.innerText = '';
      }
    }

    // Calculate the width of the select element (which is based on the width of the widest option)
    languageInput.style.width = 'fit-content';
    const originalElementWidth = `${Math.ceil(languageInput.offsetWidth) + 1}px`;

    // Fix the width of the select element
    if (languageInput.offsetWidth > 0) {
      languageInput.style.width = originalElementWidth;
    }

    // Add back all option labels
    for (const option of languageInput.options) {
      if (option.dataset.optionLabel) {
        option.textContent = option.dataset.optionLabel;
        delete option.dataset.optionLabel;
      }
    }
  }

  /**
   * Finds matches for a given search value in a country element.
   *
   * @typedef {Object} Options
   * @property {boolean} [matchLabel] - Whether to match the label.
   * @property {boolean} [matchAlias] - Whether to match the alias.
   * @property {boolean} [matchIso] - Whether to match the iso.
   * @property {boolean} [matchCurrency] - Whether to match the currency.
   * @property {boolean} [labelMatchStart] - Whether to match the label start.
   * @property {boolean} [aliasExactMatch] - Whether to match the alias exact match.
   *
   * @typedef {Object} MatchTypes
   * @property {boolean} [label] - Whether the label matches the search value.
   * @property {boolean} [alias] - Whether the alias matches the search value.
   * @property {boolean} [iso] - Whether the iso matches the search value.
   * @property {boolean} [currency] - Whether the currency matches the search value.
   *
   * @param {string} searchValue - The search value to find matches for.
   * @param {HTMLElement} countryEl - The country element to find matches in.
   * @param {Options} options - The options for the search.
   * @returns {MatchTypes} The matches found in the country element.
   */
  #findMatches(
    searchValue,
    countryEl,
    options = {
      // Which data types (label, alias, iso) to match against
      matchLabel: true,
      matchAlias: true,
      matchIso: true,
      matchCurrency: true,
      // If true, the search value must match the start of the label
      labelMatchStart: false,
      // If true, a result will not display unless the search value equals an alias in its entirety
      aliasExactMatch: false,
    }
  ) {
    let matchTypes = {};
    const { aliases, value: iso } = countryEl.dataset;

    if (options.matchLabel) {
      const countryName = normalizeString(countryEl.querySelector('.country')?.textContent ?? '');

      if (!countryName) return matchTypes;

      matchTypes.label = options.labelMatchStart
        ? countryName.startsWith(searchValue)
        : countryName.includes(searchValue);
    }

    if (options.matchCurrency) {
      const currency = normalizeString(countryEl.querySelector('.localization-form__currency')?.textContent ?? '');
      matchTypes.currency = currency.includes(searchValue);
    }

    if (options.matchIso) {
      matchTypes.iso = normalizeString(iso ?? '') == searchValue;
    }

    if (options.matchAlias) {
      const countryAliases = aliases?.split(',').map((alias) => normalizeString(alias));

      if (!countryAliases) return matchTypes;

      matchTypes.alias =
        countryAliases.length > 0 &&
        countryAliases.find((alias) =>
          options.aliasExactMatch ? alias === searchValue : alias.startsWith(searchValue)
        ) !== undefined;
    }

    return matchTypes;
  }

  /**
   * Highlights matching text in a string by wrapping it in <mark> tags.
   *
   * @param {string | null} text - The text to highlight.
   * @param {string} searchValue - The search value to highlight.
   * @returns {string} The text with matching parts wrapped in <mark> tags.
   */
  #highlightMatches(text, searchValue) {
    if (!text || !searchValue) return text ?? '';

    const normalizedText = normalizeString(text);
    const normalizedSearch = normalizeString(searchValue);
    const startIndex = normalizedText.indexOf(normalizedSearch);

    if (startIndex === -1) return text;

    const endIndex = startIndex + normalizedSearch.length;
    const before = text.slice(0, startIndex);
    const match = text.slice(startIndex, endIndex);
    const after = text.slice(endIndex);

    return `${before}<mark>${match}</mark>${after}`;
  }

  /**
   * Filters the countries based on the search value.
   */
  filterCountries() {
    const { countryList, countryListItems, liveRegion, noResultsMessage, popularCountries, resetButton, search } =
      this.formRefs;
    const { labelResultsCount } = this.dataset;
    const searchValue = normalizeString(search.value);
    let countVisibleCountries = 0;

    resetButton?.toggleAttribute('hidden', !searchValue);

    if (popularCountries) {
      popularCountries.toggleAttribute('hidden', Boolean(searchValue));
    }

    const wrapper = this.querySelector('.country-selector-form__wrapper');
    if (wrapper) {
      wrapper.classList.toggle('is-searching', !!searchValue);
    }

    for (const countryEl of countryListItems) {
      if (searchValue === '') {
        countryEl.removeAttribute('hidden');
        const countrySpan = countryEl.querySelector('.country');
        if (countrySpan) {
          // eslint-disable-next-line no-self-assign
          countrySpan.textContent = countrySpan.textContent;
        }
        countVisibleCountries++;
      } else {
        const matches = this.#findMatches(searchValue, countryEl);

        // In the future, we could reorder/rank filtered results based on the match types
        if (matches.label || matches.alias || matches.iso || matches.currency) {
          countryEl.removeAttribute('hidden');
          const countrySpan = countryEl.querySelector('.country');
          if (countrySpan) {
            countrySpan.innerHTML = this.#highlightMatches(countrySpan.textContent, searchValue);
          }
          countVisibleCountries++;
        } else {
          countryEl.setAttribute('hidden', '');
        }
      }
    }

    if (liveRegion && labelResultsCount) {
      liveRegion.innerText = labelResultsCount.replace('[count]', `${countVisibleCountries}`);
    }

    if (noResultsMessage) {
      noResultsMessage.hidden = countVisibleCountries > 0;
    }
    countryList.scrollTop = 0;
  }

  /**
   * Changes the focus of the country list items.
   *
   * @param {string} direction - The direction to change the focus.
   */
  #changeCountryFocus(direction) {
    const { countryListItems } = this.formRefs;
    const focusableItems = countryListItems.filter((item) => !item.hasAttribute('hidden'));
    const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
    const focusedItem = focusableItems[focusedItemIndex];
    let itemToFocus;

    if (direction === 'UP') {
      itemToFocus =
        focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
    } else {
      itemToFocus =
        focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
    }

    if (focusedItem) {
      focusedItem.setAttribute('aria-selected', 'false');
    }
    itemToFocus?.setAttribute('aria-selected', 'true');
    itemToFocus?.focus();
  }

  /**
   * Resets the countries filter.
   *
   * @param {Event} event - The event object.
   */
  resetCountriesFilter(event) {
    event.stopPropagation();
    this.#resetCountrySearch({ focus: true });
  }

  /**
   * Handles the keydown event for the search input.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.#onContainerKeyDown(event);
  };

  /**
   * Resets the form.
   */
  resetForm() {
    this.#resetCountrySearch();
  }

  /**
   * Resets country search state while keeping focus behavior explicit per caller.
   *
   * @param {{ focus?: boolean }} [options]
   */
  #resetCountrySearch(options = {}) {
    const { search } = this.formRefs;

    if (!search) return;

    if (search.value !== '') {
      search.value = '';
      this.filterCountries();
    }

    search.setAttribute('aria-activedescendant', '');
    if (options.focus) search.focus();
  }

  /**
   * Focuses the search input.
   */
  focusSearchInput = () => {
    const { search } = this.formRefs;

    search?.focus();
  };

  /**
   * Handles the scroll event on the country list.
   *
   * @param {Event} event - The scroll event object.
   */
  #onCountryListScroll = (event) => {
    const countryList = event.target instanceof HTMLElement ? event.target : null;
    updateCountryFilterScrollState(this, countryList);
  };
}

/**
 * A custom element that displays a dropdown localization form.
 *
 * @typedef {object} DropdownRefs
 * @property {HTMLButtonElement} button - The button element.
 * @property {HTMLDivElement} panel - The panel element.
 * @property {LocalizationFormComponent} localizationForm - The localization form component.
 *
 * @extends {Component<DropdownRefs>}
 */
class DropdownLocalizationComponent extends Component {
  get isHidden() {
    return this.refs.panel.hasAttribute('hidden');
  }

  /**
   * Toggles the panel.
   */
  toggleSelector() {
    return this.isHidden ? this.showPanel() : this.hidePanel();
  }

  /**
   * Shows the panel.
   */
  showPanel() {
    if (!this.isHidden) return;

    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener('click', this.#handleClickOutside);

    this.refs.panel.removeAttribute('hidden');
    this.refs.button.setAttribute('aria-expanded', 'true');

    onAnimationEnd(this.refs.panel, () => {
      this.#updateWidth();
      this.refs.localizationForm?.focusSearchInput();
    });
  }

  /**
   * Hides the panel.
   */
  hidePanel = () => {
    if (this.isHidden) return;

    this.removeEventListener('keyup', this.#handleKeyUp);
    document.removeEventListener('click', this.#handleClickOutside);

    this.refs.button?.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('hidden', '');
    this.refs.localizationForm?.resetForm();
  };

  /**
   * Handles the click outside event.
   *
   * @param {PointerEvent} event - The event object.
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.hidePanel();
    }
  };

  /**
   * Updates the width of the panel.
   */
  #updateWidth() {
    this.style.setProperty('--width', `${this.refs.localizationForm.offsetWidth}px`);
  }

  /**
   * Handles the keyup event.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #handleKeyUp = (event) => {
    switch (event.key) {
      case 'Escape':
        this.hidePanel();
        event.stopPropagation();
        this.refs.button?.focus();
        break;
    }
  };
}

/**
 * A custom element that displays a drawer localization form.
 *
 * @typedef {object} DrawerRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {LocalizationFormComponent} localizationForm - The localization form component.
 *
 * @extends {Component<DrawerRefs>}
 */
class DrawerLocalizationComponent extends Component {
  /**
   * Toggles the dialog.
   *
   * @param {ToggleEvent} event - The event object.
   */
  toggle(event) {
    const { target } = event;
    const { localizationForm } = this.refs;

    if (!localizationForm || !(target instanceof HTMLDetailsElement)) return;

    const countryList = localizationForm.querySelector('.country-selector-form__wrapper');

    if (target.open) {
      if (countryList) countryList.addEventListener('scroll', this.#onCountryListScroll);
      onAnimationEnd(target, localizationForm.focusSearchInput);
    } else {
      countryList?.removeEventListener('scroll', this.#onCountryListScroll);
      localizationForm.resetForm();
    }
  }

  /**
   * Handles the scroll event on the country list.
   *
   * @param {Event} event - The scroll event object.
   */
  #onCountryListScroll = (event) => {
    const countryList = event.target instanceof HTMLElement ? event.target : null;
    updateCountryFilterScrollState(this, countryList);
  };
}

if (!customElements.get('localization-form-component')) {
  customElements.define('localization-form-component', LocalizationFormComponent);
}

if (!customElements.get('dropdown-localization-component')) {
  customElements.define('dropdown-localization-component', DropdownLocalizationComponent);
}

if (!customElements.get('drawer-localization-component')) {
  customElements.define('drawer-localization-component', DrawerLocalizationComponent);
}
